import fncFetchBuilder from "./fetch/fncFetchBuilder.js";
import { allNodeNamesFromRoot, obtainNearestRootElement } from "../../../utils.js";

const LAST_CHANCE_DEFAULT_LOCALE = "en-us";

const eventListenerInterceptor = (handler, el) => event => {
  handler({
    ...event,
    // We make sure the target is always the root element just in case it originates 
    // from another element further down in the hierarchy
    target: el,
    originalTarget: event.target
  })
};

export class ComponentRegistry {
  /**
   * @type {() => import('axios').Axios}
   */
  fncFetch

  /**
   * All custom elements current on the DOM managed by this context.
   * 
   * @type {(HTMLElement & { root: boolean; })[]}
   * @private
   */
  #elementRegistry = [];

  /**
   * All active event registrations that apply to all custom elements
   * instances managed by this context.
   * 
   * 0th item in the array is the event name, the 1st is the handler, 
   * and the 2nd is an optional applicability filter
   * 
   * @type {[ string, (event: Event) => void, undefined | ((element: HTMLElement) => boolean) ][]}
   * @private
   */
  #broadcastSubscriptions = [];

  /**
   * Common attributes that should be applied to all custom elements in this context
   * 
   * @type {{ [attribute: string]: string }}
   * @private
   */
  #commonAttributes = {};

  /**
   * Services (such as data acquisition) can be registered in the loader and then
   * can be obtained by registered custom elements using the `obtainService(serviceName: string)` method
   * 
   * @type {{ [serviceName: string]: import("./types.js").ApplicationService }}
   * @private
   */
  #applicationServices = {};

  /**
   * @private
   * @type {import("./types.js").FncLoaderConfig}
   */
  #configuration;

  /**
   * @type {boolean}
   * @private
   */
  #readyCalled = false;

  /**
   * Tracks which bundles have already been loaded
   * 
   * @type {string[]}
   * @private
   */
  #loadedBundles = [];

  /**
   * This is the first method called to register configuration,
   * trigger the token refresh (which you can optionally await this
   * method to know that it worked).
   * 
   * @param {import("./types.js").FncLoaderConfig} configuration 
   */
  async loadConfiguration(configuration) {
    if (this.#configuration) {
      throw new Error(`loadConfiguration() can only be called one time`);
    }

    this.#configuration = configuration;
    this.#commonAttributes = configuration.commonAttributes || {};

    // Add listener for root elements as they get added to the page
    this.#registerElementHandlers();

    // Initialize the lazy load watcher
    this.#initializeLazyLoadWatcher();

    // Expose this class and the fncFetch if debugging is enabled
    if (configuration.debug) {
      window.fncFetch = this.fncFetch;
      window.fncComponentRegistry = this;
    }

    this.fncFetch = fncFetchBuilder(configuration.apiUrl, configuration.tokenProvider);
    if (!configuration.skipInitialTokenUpdate) {
      await this.fncFetch.triggerTokenUpdate();
    }
  }

  /**
   * This will start the loading of apps on the page. loadConfiguration() and ready()
   * are split so that the feature team can have a chance to register any services or
   * set up any event wiring prior to the apps' bundles being dynamically imported.
   */
  async ready() {
    if (this.#readyCalled) {
      throw new Error(`ready() can only be called one time`);
    }
    this.#readyCalled = true;
    const configuration = this.#configuration;

    // Make sure any global CSS is loaded first
    for (const css of (configuration.globalCss || [])) {
      const style = document.createElement("link");
      style.rel = "stylesheet";
      style.type = "text/css";
      style.href = new URL(css, this.#assetsBaseUrl);
      style.media = "all";
      document.head.appendChild(style);
    }
    // And decorate all manifest entries to include any shadowDOMCss entries
    for (const app of (Object.values(configuration.apps) || [])) {
      app.css = [
        ...(app.css || []),
        ...(configuration.shadowDOMCss || []).map(css => new URL(css, this.#assetsBaseUrl))
      ];
    }

    // Load any components the customer has explicitly decided to include
    // (notwithstanding their autoLoad or lazyLoad values)
    for (const name of (configuration.preLoadApps || [])) {
      this.loadApp(name);
    }

    // Load any bundles specified by the customer
    await this.#loadBundles();

    // Load all of the components with load being auto
    Object.keys(configuration.apps)
      .filter(appName => configuration.apps[appName].load === "auto" && (configuration.preLoadApps || []).indexOf(appName) < 0)
      .forEach(appName => this.loadApp(appName));

    // This will load any apps marked "lazy" that are currently on the DOM
    // and begin watching for new elements being added to the page
    this.#startLazyLoadWatcher();
  }

  /**
   * Dynamically imports the specified application's bundle
   * 
   * @param {string} name 
   * @returns {any} The dynamically imported module
   */
  async loadApp(name) {
    if (customElements.get(name)) {
      console.log(`[ComponentRegistry] Skipping loadApp("${name}"). It appears to already be loaded`);
      return;
    }
    const entrypoint = this.#configuration?.apps?.[name]?.entrypoint;
    return (await import(/* @vite-ignore */entrypoint)).default;
  }

  /**
   * This will add an event to all root elements registered now and
   * in the future subject to the `applicabilityFilter` if provided.
   * 
   * @param {string} eventName The name of the event
   * @param {(event: Event) => void} handler 
   * @param {(element: HTMLElement) => boolean} applicabilityFilter 
   */
  addEventListener(eventName, handler, applicabilityFilter = () => true) {
    this.#rootElements.forEach(el => applicabilityFilter(el) && el.addEventListener(eventName, eventListenerInterceptor(handler, el)));
    this.#broadcastSubscriptions.push([eventName, handler, applicabilityFilter]);
  }

  /**
   * 
   * @param {{ [attribute: string]: string }} obj 
   */
  updateCommonAttributes(obj) {
    // NOTE: This will add any new and update any existing values, but will _not_ remove
    this.#commonAttributes = {
      ...this.#commonAttributes,
      ...obj
    };
    // Forcibly replace 
    this.#rootElements.forEach(el => this.#applyCommonAttributes(el, Object.keys(obj)));
  }

  /**
   * 
   * @param {string} serviceName 
   * @param {import("./types.js").ApplicationService} service 
   */
  registerApplicationService(serviceName, service) {
    this.#applicationServices[serviceName] = service;
  }

  /**
   * Adds a bundle to be loaded dynamically to the configuration
   * and triggers the loading process
   * 
   * @param {string} bundleName The name of the bundle to load
   * @returns {Promise<void>} Promise that resolves once the loading is complete
   */
  async loadBundle(bundleName) {
    this.#configuration.loadBundles = Array.from(new Set([
      ...(this.#configuration.loadBundles || []),
      bundleName
    ]));
    await this.#loadBundles();
  }

  /**
   * The current state of the FNC Common configuration
   */
  get configuration() {
    return Object.freeze(this.#configuration);
  }

  /**
   * The current token within the `fncFetch` instance
   */
  get currentToken() {
    return this.fncFetch.currentToken;
  }

  /**
   * Apply common attributes to an element. By default it will avoid
   * replacing existing attributes when present unless they are present
   * in the `forcedUpdates` array.
   * 
   * @private
   * @param {HTMLElement} el 
   * @param {string[]} forcedUpdates 
   */
  #applyCommonAttributes(el, forcedUpdates = []) {
    Object.entries(this.#commonAttributes || {})
      .forEach(([attribute, value]) => {
        // Only replace values not already set or are in the `forcedUpdates` list
        if (!el.getAttribute(attribute) || forcedUpdates.indexOf(attribute) >= 0) {
          el.setAttribute(attribute, value);
        }
      });
  }

  /**
   * Loads any bundles not yet already loaded
   */
  async #loadBundles() {
    await Promise.all(
      (this.#configuration.loadBundles || [])
        .filter(bundleToLoad => this.#loadedBundles.indexOf(bundleToLoad) < 0)
        .map(bundleToLoad => {
          const bundle = this.#configuration.bundles[bundleToLoad];
          if (!bundle) {
            console.warn(`[ComponentRegistry] Bundle specified in loadBundles does not appear to exist in application: ${bundleToLoad}`);
            return Promise.resolve();
          }
          this.#loadedBundles.push(bundleToLoad);
          return import(/* @vite-ignore */ bundle.entrypoint);
        })
    );
  }

  /**
   * @private
   */
  #registerElementHandlers() {
    const connectedHandler = rootHandler => e => {
      const { detail: { classInstance } } = e;
      const nodeName = classInstance.nodeName.toLowerCase();

      // Grab the manifest entry for this root element
      const manifestEntry = this.#configuration.apps[nodeName];
      if (rootHandler && !manifestEntry) {
        if (this.#configuration.importsAlreadyProvided) {
          console.warn(`[ComponentRegistry] Note that since imports are already provided and an app configuration was not found for ${nodeName}, we won't have a manifestEntry to inject. This is a bug if you're not expecting to see this warning.`);
        } else {
          console.error(`[ComponentRegistry] Could not find manifest entry for ${nodeName}, skipping its registration. The 'fnc-root-element-connected' event is reserved for root elements only. Please use 'fnc-sub-element-connected' for elements only looking for dependency injection from the loader.`);
          return;
        }
      }
      classInstance.manifestEntry = manifestEntry;

      // Whether or not we're registered as a root element
      classInstance.root = rootHandler;

      // Register element in registry
      this.#elementRegistry.push(classInstance);

      // The following only apply to root elements (that is, those directly consumable by the client)
      if (rootHandler) {
        // Apply common attributes
        this.#applyCommonAttributes(classInstance);

        // Apply any existing broadcastSubscriptions
        this.#broadcastSubscriptions.forEach(([eventName, handler, applicabilityFilter]) => {
          if (applicabilityFilter(classInstance)) {
            classInstance.addEventListener(eventName, eventListenerInterceptor(handler, classInstance));
          }
        });

        // If a locale attribute has not been specified, then apply the default
        // We use attributes as our source of truth so that any changes can be observed
        if (!classInstance.getAttribute("locale")) {
          if (!this.#configuration.defaultLocale) {
            console.warn(`[ComponentRegistry] A default locale has not been passed to initWebComponents(). Please correct this. Using ${LAST_CHANCE_DEFAULT_LOCALE} for now.`)
          }
          classInstance.setAttribute("locale", this.#configuration.defaultLocale ?? LAST_CHANCE_DEFAULT_LOCALE);
        }
      }

      // --- Everything below this line applies to all registered elements ---

      // Note that the component needs to assume this isn't available until the component gets mounted
      classInstance.fncFetch = this.fncFetch;

      // Include a reference to the original configuration
      classInstance.fncConfig = this.#configuration;

      // A service locator for registered elements
      classInstance.obtainService = this.#obtainService(classInstance).bind(this);

      // A flag for the elements to check if their registered event succeeded
      classInstance.contextRegistered = true;

      // The observer we registered on init doesn't see shadow DOM content,
      // so if this element has a shadow root, we also observe it.
      // TODO: Unregister it on disconnectedCallback too?
      if (classInstance.shadowRoot) {
        this.observer?.observe(
          classInstance.shadowRoot,
          { attributes: false, childList: true, subtree: true }
        );
      }
    };

    const disconnectedHandler = rootHandler => e => {
      const { detail: { classInstance } } = e;

      const i = this.#elementRegistry.findIndex(e => e === classInstance);
      if (i >= 0) {
        this.#elementRegistry.splice(i, 1);
      } else {
        console.warn(`[ComponentRegistry] couldn't find disconnected element in elementRegistry`);
      }
    };

    document.body.addEventListener("fnc-root-element-connected", connectedHandler(true));
    document.body.addEventListener("fnc-sub-element-connected", connectedHandler(false));

    document.body.addEventListener("fnc-root-element-disconnected", disconnectedHandler(true));
    document.body.addEventListener("fnc-sub-element-disconnected", disconnectedHandler(false));
  }

  /**
   * @private
   */
  #initializeLazyLoadWatcher() {
    this.observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        // we should only get childList updates, but just in case
        if (mutation.type !== "childList") {
          continue;
        }

        for (const addedNode of mutation.addedNodes) {
          // The custom element may not be the root node, so we need to
          // graph all unique node names from the root `addedNode` and
          // cross reference that across the list of lazy-load elements
          const nodeNames = allNodeNamesFromRoot(addedNode);
          this.#lazyLoadElements
            .filter(e => nodeNames.indexOf(e) >= 0)
            .forEach(e => this.loadApp(e));
        }
      }
    });
  }

  /**
   * Start watching on the configured root element (`document.body` otherwise)
   * @private
   */
  #startLazyLoadWatcher() {
    // If our lazy loaded element is already on the DOM then we
    // should make sure it gets loaded too
    this.#lazyLoadElements
      .filter(el => !!document.querySelector(el))
      .forEach(el => this.loadApp(el));

    this.observer.observe(
      this.#configuration.rootElement || document.body,
      { attributes: false, childList: true, subtree: true }
    );
  }

  /**
   * @private
   * @param {HTMLElement} customElementInstance 
   * @returns {(serviceName: string) => any}
   */
  #obtainService(customElementInstance) {
    return (serviceName) => {
      const service = this.#applicationServices[serviceName];
      if (!service) {
        console.error(`[ComponentRegistry] Could not find requested application service: ${serviceName}`);
        return;
      }

      // If a service is registered as a function, we assume it's a factory and dynamically instantiate it
      // with a reference to the custom element instance and to this ComponentRegistry
      if (service instanceof Function) {
        if (!customElementInstance.root) {
          // If we're not a root element (that is, we're a sub-element), then
          // we need to find our nearest root element to pass to the builder
          // (Only root elements can build services)
          const nearestRoot = obtainNearestRootElement(customElementInstance);
          if (!nearestRoot) {
            console.warn(`[ComponentRegistry] obtainService() called from a sub-component that couldn't find a nearest root. Custom element instance:`, customElementInstance);
            return;
          }
          return service(nearestRoot, this);
        }
        return service(customElementInstance, this);
      }
      // Otherwise, return it verbatim
      return service;
    };
  }

  get #assetsBaseUrl() {
    const { assetsUrl } = this.#configuration;
    // undefined is a special case -- in that situation we use the relative location
    // of the current script (which is often OK)
    if (assetsUrl === undefined) {
      return import.meta.url;
    }
    return assetsUrl?.endsWith("/") ? assetsUrl : `${assetsUrl}/`;
  }

  get #rootElements() {
    return this.#elementRegistry.filter(e => e.root);
  }

  get #loadedBundleApps() {
    if (!this.#configuration.loadBundles?.length) {
      return [];
    }

    return Array.from(new Set(this.#configuration.loadBundles
      .map(bundleName => (this.#configuration.bundles || {})[bundleName])
      .map(({ apps }) => apps)
      .flat()
    ));
  }

  get #lazyLoadElements() {
    return Object.keys(this.#configuration.apps)
      .filter(c =>
        this.#configuration.apps[c].load === "lazy"
        // Don't enable lazy loading for elements that are already marked as being pre-loaded by the client
        && (this.#configuration.preLoadApps || []).indexOf(c) < 0
        // Don't enable lazy loading for elements that are already loaded as a part of a bundle
        && (this.#loadedBundleApps).indexOf(c) < 0
      );
  }
}